"use client";
import { usePathname, useRouter } from "next/navigation";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import { useLoggedIn } from "@/store/useAuthStore";
import { useCartStore } from "@/store/useCartStore";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonIcon from "@mui/icons-material/Person";

// Routes where the primary CTA (checkout / payment / order confirmation)
// should own the bottom of the screen — floating nav would distract.
const HIDDEN_SECONDS = new Set([
  "checkout",
  "order-payment",
  "orderconfirmation",
]);

export default function MobileBottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const dict = useDictionary();
  const { auth } = useLoggedIn();
  const { cart } = useCartStore();

  const segments = pathname.split("/");
  const lang = segments[1] || "en";
  const second = segments[2] || "";

  if (HIDDEN_SECONDS.has(second)) return null;

  const cartCount = cart?.items?.length ?? 0;
  const isHome = pathname === `/${lang}` || pathname === `/${lang}/`;
  const isCart = second === "cart";
  // Broad: any "my account" area (profile page, orders, wishlist, wallet…)
  // keeps the Account tab highlighted so users know where they are.
  const isAccount = new Set([
    "user",
    "profile",
    "orders",
    "address",
    "wishlist",
    "wallet",
    "loyalty-program",
    "rating-and-review",
    "account-deletion-and-privacy",
    "giftcard-check",
    "login",
  ]).has(second);

  const openSearch = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("camouflage:open-search"));
    }
  };

  const goAccount = () => {
    router.push(auth ? `/${lang}/user` : `/${lang}/login`);
  };

  const items: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    activeIcon: React.ReactNode;
    onClick: () => void;
    active: boolean;
    badge?: number;
  }> = [
    {
      key: "home",
      label: dict.headerText.home,
      icon: <HomeOutlinedIcon />,
      activeIcon: <HomeIcon />,
      onClick: () => router.push(`/${lang}`),
      active: isHome,
    },
    {
      key: "search",
      label: dict.headerText.search,
      icon: <SearchIcon />,
      activeIcon: <SearchIcon />,
      onClick: openSearch,
      active: false,
    },
    {
      key: "cart",
      label: dict.headerText.cart,
      icon: <ShoppingCartOutlinedIcon />,
      activeIcon: <ShoppingCartIcon />,
      onClick: () => router.push(`/${lang}/cart`),
      active: isCart,
      badge: cartCount,
    },
    {
      key: "account",
      label: dict.headerText.account,
      icon: <PersonOutlineIcon />,
      activeIcon: <PersonIcon />,
      onClick: goAccount,
      active: isAccount,
    },
  ];

  return (
    <>
      {/* Spacer in normal flow so page content isn't hidden under the
          fixed nav on mobile. Also covers iOS safe-area padding. */}
      <div
        aria-hidden="true"
        className="lg:hidden h-[calc(64px+env(safe-area-inset-bottom))]"
      />
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[999] bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="flex justify-around items-stretch h-16">
          {items.map((it) => (
            <li key={it.key} className="flex-1">
              <button
                type="button"
                onClick={it.onClick}
                aria-label={it.label}
                aria-current={it.active ? "page" : undefined}
                className={`w-full h-full flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  it.active ? "text-primary" : "text-gray-600"
                }`}
              >
                <div className="relative">
                  {it.active ? it.activeIcon : it.icon}
                  {it.badge != null && it.badge > 0 && (
                    <span className="absolute -top-1 ltr:-right-2 rtl:-left-2 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                      {it.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] leading-tight">{it.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
